import { pipe } from "fp-ts/lib/function";
import { nonEmptyArray, option } from "fp-ts";
import { AttributeOnClickData } from "./attributeToPicker";
import { EnrichedSwarovskiSingleProductFrontend } from "@swo/storefront";
import { nonEmpty } from "../../../../utils/fp/small-fp-utils";
import { attributeArrayUniq } from "../state/attributes";
import { PDPSwarovskiProductFrontend } from "../state/types";

export function mapToPDPSwarovskiProductFrontend(
  product: EnrichedSwarovskiSingleProductFrontend,
): PDPSwarovskiProductFrontend {
  const variants = pipe(
    product.variants,
    nonEmptyArray.map(variant => {
      const unique = pipe(
        (variant.attributes?.unique || []).flatMap((attribute, idx) => {
          const allProductUniqueAttributes = product.variants.map(v => v.attributes?.unique[idx]).filter(nonEmpty);

          const uniqueAttributesWithDefinedValue = pipe(
            allProductUniqueAttributes.flatMap(a => (a.value ? [a] : [])),
            attributeArrayUniq,
          );

          if (uniqueAttributesWithDefinedValue.length > 0) {
            return [attribute];
          } else {
            return [];
          }
        }),
      );

      return {
        ...variant,
        attributes: {
          showcased: variant.attributes?.showcased || [],
          unique: pipe(unique, nonEmptyArray.fromArray, option.toUndefined),
          filter: variant.attributes?.filter || [],
        },
      };
    }),
  );

  return {
    ...product,
    variants,
  };
}

export type PDPPickerState = "selected" | "disabled" | "selectable";

export type VariantPickerVariation_Color = { tag: "color-picker"; colorHex: string } & VariantPickerSharedProperties;

export type VariantPickerVariation_Image = {
  tag: "image-picker";
  image?: { asset: string; name: string };
} & VariantPickerSharedProperties;

export type VariantPickerVariation_ClothSize = {
  tag: "cloth-size-picker";
  size: { data: string; label: string };
} & VariantPickerSharedProperties;

export type VariantPickerVariation_PriceOnly = {
  tag: "price-only-picker";
} & VariantPickerSharedProperties;

export type VariantPickerVariation_Plain = { tag: "plain-picker" } & VariantPickerSharedProperties;

export type VariantPickerVariation =
  | VariantPickerVariation_Color
  | VariantPickerVariation_Image
  | VariantPickerVariation_ClothSize
  | VariantPickerVariation_PriceOnly
  | VariantPickerVariation_Plain;

export type VariantPickerCollection<T extends { tag: string }> = Pick<T, "tag"> & {
  values: Array<Omit<T, "tag">>;
};

export type VariantPickerVariationCol =
  | VariantPickerCollection<VariantPickerVariation_Color>
  | VariantPickerCollection<VariantPickerVariation_Image>
  | VariantPickerCollection<VariantPickerVariation_ClothSize>
  | VariantPickerCollection<VariantPickerVariation_PriceOnly>
  | VariantPickerCollection<VariantPickerVariation_Plain>;

export type VariantPickerSharedProperties = {
  name: string;
  additionalText?: string;
  target:
    | {
        tag: "link";
        link: string;
      }
    | {
        tag: "click";
        onClick: AttributeOnClickData;
      }
    | { tag: "disabled" };
  state: PDPPickerState;
  isNew: boolean;
};
